/**
 * §281: Browser Artifact Signal Types and Utilities — SDK public surface.
 *
 * Pure types and formatting/filtering utilities for browser observability
 * signal snapshots. No Playwright, no webapp runtime dependencies.
 *
 * External SDK consumers (component jobs, MCP tool handlers, test helpers)
 * can use these types and utilities to process browser probe signals without
 * needing the webapp testing surface.
 */

import type { BrowserHttpErrorEntry } from './browserHttpErrors'

export type {
    BrowserHttpErrorAllowlistRule,
    BrowserHttpErrorEntry,
} from './browserHttpErrors'

// ── Signal Types ──────────────────────────────────────────────────────

export interface BrowserConsoleErrorEntry {
    readonly source?: string
    readonly text: string
    readonly url?: string
    readonly line?: number
    readonly column?: number
}

export interface BrowserNetworkFailureEntry {
    readonly url: string
    readonly method?: string
    readonly resourceType?: string
    readonly errorText: string
}

export interface BrowserProbeSignalSnapshot {
    readonly consoleErrors: string[]
    readonly networkFailures: BrowserNetworkFailureEntry[]
    readonly httpErrors: BrowserHttpErrorEntry[]
}

// ── Formatting Utilities ──────────────────────────────────────────────

export function formatConsoleError(entry: BrowserConsoleErrorEntry): string {
    const source = entry.source?.trim() || 'console'
    const message = entry.text.trim() || '(empty console error)'
    const location = formatLocation(entry.url, entry.line, entry.column)

    return location ? `[${source}] ${message} (${location})` : `[${source}] ${message}`
}

export function formatConsoleErrors(entries: readonly BrowserConsoleErrorEntry[]): string {
    return entries.map((entry, index) => `${index + 1}. ${formatConsoleError(entry)}`).join('\n')
}

export function formatNetworkFailure(entry: BrowserNetworkFailureEntry): string {
    const method = entry.method ?? 'GET'
    const resourceType = entry.resourceType ? ` [${entry.resourceType}]` : ''
    return `${method} ${entry.url} -> ${entry.errorText}${resourceType}`
}

export function formatNetworkFailures(entries: readonly BrowserNetworkFailureEntry[]): string {
    return entries.map((entry, index) => `${index + 1}. ${formatNetworkFailure(entry)}`).join('\n')
}

// ── Filter/Decision Helpers ───────────────────────────────────────────

export function shouldAttachBrowserArtifacts(status?: string, expectedStatus?: string): boolean {
    if (!status || status === 'skipped') {
        return false
    }

    if (expectedStatus && status === expectedStatus) {
        return false
    }

    return true
}

export function shouldCaptureNetworkFailure(errorText: string, canceled?: boolean): boolean {
    if (canceled) {
        return false
    }

    return errorText !== 'net::ERR_ABORTED'
}

export function shouldFailForUnexpectedHttpErrors(
    entries: readonly BrowserHttpErrorEntry[],
    status?: string,
    expectedStatus?: string,
): boolean {
    if (entries.length === 0) {
        return false
    }

    if (status !== 'passed') {
        return false
    }

    return !expectedStatus || expectedStatus === 'passed'
}

// ── Internal Helpers ──────────────────────────────────────────────────

function formatLocation(url?: string, line?: number, column?: number): string | null {
    if (!url) {
        return null
    }

    if (line == null) {
        return url
    }

    if (column == null) {
        return `${url}:${line}`
    }

    return `${url}:${line}:${column}`
}
